import React, { useState, useEffect } from 'react';
import profilePhoto from '../assets/images/ProfilePhotoScaylor.png';
import { useNavigate } from 'react-router-dom';
import ContentBackground from './ContentBackground';
import halfOrb from '../assets/images/BackgroundPinkRedOrbFull.svg';
import { US_STATES } from '../utils/states';
import { TIMEZONES } from '../utils/timezones';
import NotificationBanner from './NotificationBanner';
import ProfilePhoto from './ProfilePhoto';
import { jwtDecode } from 'jwt-decode';

const Settings = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('profile');
    const [selectedCountry, setSelectedCountry] = useState('United States');
    const [selectedTimezone, setSelectedTimezone] = useState('America/New_York');
    const [selectedState, setSelectedState] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [decodedToken, setDecodedToken] = useState('');
    const [notification, setNotification] = useState(null);
    const [organizationDetails, setOrganizationDetails] = useState({
      name: '',
      website: '',
      address: '',
      phone: '',
    });
    const [userProfile, setUserProfile] = useState({
      name: '',
      email: ''
    });
    const [passwordData, setPasswordData] = useState({
      currentPassword: '',
      newPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
      current: false,
      new: false
    });

    const [teamMembers, setTeamMembers] = useState([]);

    const [inviteForm, setInviteForm] = useState({
      email: '',
      role: 'member'
    });

    useEffect(() => {
      const token = localStorage.getItem('token');
      if (token) {
        const decodedToken = jwtDecode(token);
        setDecodedToken(decodedToken);
      }
    }, []);

    // Fetch organization details when component mounts
    useEffect(() => {
      const fetchOrganizationDetails = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/organization`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            console.log('Organization API response:', data);  // See what we get
            if (data.organization) {
              // Parse address to get city and state
              const addressParts = data.organization.address.split(',').map(part => part.trim());
              const stateZipParts = addressParts[2]?.split(' ') || [];
              const state = stateZipParts[0];
              const zipCode = stateZipParts[1];

              setOrganizationDetails({
                name: data.organization.name || '',
                website: data.organization.website || '',
                address: addressParts[0] || '',
                phone: data.organization.phone || '',
              });
              setSelectedCountry(data.organization.country || 'United States');
              setSelectedTimezone(data.organization.timezone || 'America/New_York');
              setSelectedState(state || '');
              setSelectedCity(addressParts[1] || '');
              // Combine members and pending invites
              const activeMembers = data.members.map(member => ({
                ...member,
                status: 'Active'
              }));

              const pendingMembers = data.pendingInvites.map(invite => ({
                id: invite.id,  // This is the ID we'll use for deleting invites
                email: invite.email,
                role: invite.role,
                status: 'Pending'
              }));

              setTeamMembers([...activeMembers, ...pendingMembers]);
            }
          } else {
            console.error('Failed to fetch organization details');
          }
        } catch (error) {
          console.error('Error fetching organization details:', error);
        }
      };

      if (activeTab === 'organization') {
        fetchOrganizationDetails();
      }
    }, [activeTab, navigate]);

    // Fetch user profile when profile tab is active
    useEffect(() => {
      const fetchUserProfile = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/me`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            setUserProfile({
              name: data.name || '',
              email: data.email || ''
            });
          } else {
            console.error('Failed to fetch user profile');
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }

        
      };


      if (activeTab === 'profile') {
        fetchUserProfile();
      }
    }, [activeTab, navigate]);

    const handleCountryChange = (event) => {
        setSelectedCountry(event.target.value);
    };

    const handleTimezoneChange = (event) => {
        setSelectedTimezone(event.target.value);
    };

    const handleStateChange = (event) => {
        setSelectedState(event.target.value);
    };

    const handleCityChange = (event) => {
        setSelectedCity(event.target.value);
    };

    const handleOrganizationChange = (e) => {
      setOrganizationDetails({
        ...organizationDetails,
        [e.target.name]: e.target.value
      });
    };

    const handleSaveOrganization = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Construct the full address
      const fullAddress = `${organizationDetails.address}, ${selectedCity}, ${selectedState}`;

      const organizationData = {
        name: organizationDetails.name,
        country: selectedCountry,
        timezone: selectedTimezone,
        address: fullAddress,
        phone: organizationDetails.phone,
        website: organizationDetails.website
      };

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/organization/update`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          body: JSON.stringify(organizationData),
        });

        if (response.ok) {
          setNotification({
            type: 'success',
            message: 'Organization details updated successfully'
          });
          setTimeout(() => setNotification(null), 3000); // Hide after 3 seconds
        } else {
          const error = await response.json();
          setNotification({
            type: 'error',
            message: 'Failed to update organization: ' + (error.message || 'Unknown error')
          });
          setTimeout(() => setNotification(null), 3000); // Hide after 3 seconds
        }
      } catch (error) {
        setNotification({
          type: 'error',
          message: 'Network error: ' + error.message
        });
        setTimeout(() => setNotification(null), 3000); // Hide after 3 seconds
      }
    };



    // Add handler for profile changes
    const handleProfileChange = (e) => {
      setUserProfile({
        ...userProfile,
        [e.target.name]: e.target.value
      });
    };

    // Add save profile function
    const handleSaveProfile = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/profile/update`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          body: JSON.stringify(userProfile),
        });

        if (response.ok) {
          setNotification({
            type: 'success',
            message: 'Profile updated successfully'
          });
          setTimeout(() => setNotification(null), 3000);
        } else {
          const error = await response.json();
          setNotification({
            type: 'error',
            message: 'Failed to update profile: ' + (error.message || 'Unknown error')
          });
          setTimeout(() => setNotification(null), 3000);
        }
      } catch (error) {
        setNotification({
          type: 'error',
          message: 'Network error: ' + error.message
        });
        setTimeout(() => setNotification(null), 3000);
      }
    };

    const handlePasswordChange = (e) => {
      setPasswordData({
        ...passwordData,
        [e.target.name]: e.target.value
      });
    };

    const handleChangePassword = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      if (!passwordData.currentPassword || !passwordData.newPassword) {
        setNotification({
          type: 'error',
          message: 'Both current and new password are required'
        });
        setTimeout(() => setNotification(null), 3000);
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/password`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            currentPassword: passwordData.currentPassword,
            newPassword: passwordData.newPassword
          }),
        });

        if (response.ok) {
          setNotification({
            type: 'success',
            message: 'Password updated successfully'
          });
          // Clear password fields after successful update
          setPasswordData({
            currentPassword: '',
            newPassword: ''
          });
        } else {
          const error = await response.json();
          setNotification({
            type: 'error',
            message: 'Failed to update password: ' + (error.message || 'Unknown error')
          });
        }
      } catch (error) {
        setNotification({
          type: 'error',
          message: 'Network error: ' + error.message
        });
      }
      setTimeout(() => setNotification(null), 3000);
    };

    const handleInviteChange = (e) => {
      setInviteForm({
        ...inviteForm,
        [e.target.name]: e.target.value
      });
    };

    const handleInviteMember = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      if (!inviteForm.email) {
        setNotification({
          type: 'error',
          message: 'Email is required'
        });
        setTimeout(() => setNotification(null), 3000);
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/invites`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            email: inviteForm.email,
            role: inviteForm.role
          }),
        });

        if (response.ok) {
          const inviteData = await response.json();
          
          setNotification({
            type: 'success',
            message: 'Invitation sent successfully'
          });
          
          // Add the new invite to the team members list locally
          const newMember = {
            id: Date.now(),
            email: inviteForm.email,
            role: inviteForm.role,
            status: 'Pending'
          };
          setTeamMembers([...teamMembers, newMember]);
          
          // Clear the form
          setInviteForm({
            email: '',
            role: 'member'
          });
        } else {
          const error = await response.json();
          setNotification({
            type: 'error',
            message: 'Failed to send invite: ' + (error.message || 'Unknown error')
          });
        }
      } catch (error) {
        console.error('Error sending invite:', error);
        setNotification({
          type: 'error',
          message: 'Network error: ' + error.message
        });
      }
      setTimeout(() => setNotification(null), 3000);
    };

    const removeMember = async (member) => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/members/${member.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          setTeamMembers(teamMembers.filter(m => m.id !== member.id));
          setNotification({
            type: 'success',
            message: 'Member removed successfully'
          });
        } else {
          setNotification({
            type: 'error',
            message: 'Failed to remove member'
          });
        }
      } catch (error) {
        setNotification({
          type: 'error',
          message: 'Network error: ' + error.message
        });
      }
    };

    const deleteInvite = async (member) => {
      console.log('Deleting invite for member:', member);  // See full member object
      
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        // Just use id as per API docs
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/invites/${member.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          setTeamMembers(teamMembers.filter(m => m.id !== member.id));
          setNotification({
            type: 'success',
            message: 'Invitation cancelled successfully'
          });
        } else {
          const errorData = await response.json();
          console.error('API Error:', errorData);
          setNotification({
            type: 'error',
            message: 'Failed to cancel invitation'
          });
        }
      } catch (error) {
        console.error('Error cancelling invitation:', error);
        setNotification({
          type: 'error',
          message: 'Network error: ' + error.message
        });
      }
    };

    return (
      <div className="p-8">
        {notification && (
          <NotificationBanner
            type={notification.type}
            message={notification.message}
            onClose={() => setNotification(null)}
          />
        )}
        <div className="flex [&_img[alt='pink_orb']]:opacity-100">
            <ContentBackground>
              <button 
                onClick={() => navigate('/')} 
                className="text-gray-600 mb-4 flex items-center hover:text-gray-800"
              >
                ← Back
              </button>
              <div className="flex justify-between items-center mb-8">
                <h1 className="text-2xl font-bold">Settings</h1>
                <ProfilePhoto />
              </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-md p-8 flex gap-4 mb-4">
        <button
          onClick={() => setActiveTab('profile')}
          className={`px-6 py-2 rounded-full ${activeTab === 'profile' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        >
          Profile
        </button>

        <button
          onClick={() => setActiveTab('organization')}
          className={`px-6 py-2 rounded-full ${activeTab === 'organization' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        >
          Organization
        </button>
      </div>

      {/* Profile Details Section */}
      {activeTab === 'profile' && (
        <>
          <div className="rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-6">Profile Details</h2>
            <div className="bg-white border-gray-800 rounded-lg shadow-md p-8 flex gap-4 mb-4 space-y-4">
              <div className="block space-y-4">
                <div className="flex-1 mb-4">
                  <label className="block text-sm mb-2">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={userProfile.name}
                    onChange={handleProfileChange}
                    className="w-96 p-2 border rounded-lg"
                    placeholder="Enter full name"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={userProfile.email}
                    onChange={handleProfileChange}
                    className="w-96 p-2 border rounded-lg"
                    placeholder="Enter email"
                    disabled
                  />
                </div>
                <div className="flex items-center gap-3">
                  <ProfilePhoto />
                  <button className="text-sm text-gray-600">Change Profile Photo</button>
                </div>
                <div className="mt-4">
                  <button
                    onClick={handleSaveProfile}
                    className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    Save Profile
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Change Password Section */}
          <div className="rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-6">Change Password</h2>
            <div className="bg-white border-gray-800 rounded-lg shadow-md p-8 space-y-4 mb-8">
              <div>
                <label className="block text-sm mb-2">Current Password</label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? "text" : "password"}
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    className="w-full p-2 border rounded-lg pr-20"
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-600 hover:text-gray-800 bg-transparent px-2 py-1"
                  >
                    {showPasswords.current ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
              <div className="pb-4">
                <label className="block text-sm mb-2">New Password</label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? "text" : "password"}
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className="w-full p-2 border rounded-lg pr-20"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-600 hover:text-gray-800 bg-transparent px-2 py-1"
                  >
                    {showPasswords.new ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
              <div>
                <button
                  onClick={handleChangePassword}
                  className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Update Password
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      {activeTab === 'organization' && (
        <div className="rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-6">Organization Details</h2>
          <div className="bg-white border-gray-800 rounded-lg shadow-md p-8 mb-4">
            <div className="flex gap-8">
              <div className="space-y-4">
                <div className="flex-1 mb-4">
                  <label className="block text-sm mb-2">Organization Name</label>
                  <input
                    type="text"
                    name="name"
                    value={organizationDetails.name}
                    onChange={handleOrganizationChange}
                    className="w-96 p-2 border rounded-lg"
                    placeholder="Enter organization name"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm mb-2">Timezone</label>
                  <select 
                    value={selectedTimezone}
                    onChange={handleTimezoneChange}
                    className="w-96 p-2 border rounded-lg text-gray-600"
                  >
                    {TIMEZONES.map(timezone => (
                      <option key={timezone.value} value={timezone.value}>
                        {timezone.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-sm mb-2">Website</label>
                  <input
                    type="text"
                    name="website"
                    value={organizationDetails.website}
                    onChange={handleOrganizationChange}
                    className="w-96 p-2 border rounded-lg text-gray-600"
                    placeholder="Enter website URL"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm mb-2">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={organizationDetails.address}
                    onChange={handleOrganizationChange}
                    className="w-96 p-2 border rounded-lg text-gray-600"
                    placeholder="Enter street address"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm mb-2">City</label>
                  <input 
                    type="text"
                    value={selectedCity}
                    onChange={handleCityChange}
                    className="w-96 p-2 border rounded-lg text-gray-600"
                    placeholder="Enter city"
                  />
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex-1">
                  <label className="block text-sm mb-2">Country</label>
                  <select 
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    className="w-96 p-2 border rounded-lg text-gray-600"
                  >
                    <option value="United States">United States</option>
                    <option value="Canada">Canada</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Australia">Australia</option>
                    <option value="New Zealand">New Zealand</option>
                    <option value="South Africa">South Africa</option>
                    <option value="India">India</option>
                    <option value="China">China</option>
                    <option value="Japan">Japan</option>
                    <option value="Korea">Korea</option>
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-sm mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={organizationDetails.phone}
                    onChange={handleOrganizationChange}
                    className="w-96 p-2 border rounded-lg text-gray-600"
                    placeholder="Enter phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2 font-Gilroy font-medium">State</label>
                  <select 
                    value={selectedState}
                    onChange={handleStateChange}
                    className="w-full p-2 border rounded-lg text-gray-600"
                  >
                    <option value="">Select State</option>
                    {US_STATES.map(state => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="mt-12">
              <button
                onClick={handleSaveOrganization}
                className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors"
              >
                Save Organization Details
              </button>
            </div>
          </div>
          
          {/* Team Members Section */}
          <h2 className="text-xl font-semibold mb-6">Team Members</h2>
          <div className="bg-white border-gray-800 rounded-lg shadow-md p-8">
            {/* Invite Member Row */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-grow">
                <input 
                  type="email" 
                  name="email"
                  value={inviteForm.email}
                  onChange={handleInviteChange}
                  placeholder="User@scaylor.com" 
                  className="w-full p-2 border rounded-lg"
                />
              </div>
              <select 
                name="role"
                value={inviteForm.role}
                onChange={handleInviteChange}
                className="p-2 border rounded-lg w-32"
              >
                <option value="admin">Admin</option>
                <option value="member">Member</option>
              </select>
              <button 
                onClick={handleInviteMember}
                className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
              >
                Invite Member
              </button>
            </div>

            {/* Table Header */}
            <div className="grid grid-cols-4 gap-4 py-2 border-b text-sm text-gray-600">
              <div>Email</div>
              <div>Role</div>
              <div>Status</div>
              <div>Actions</div>
            </div>

            {/* Table Rows */}
            {teamMembers.map((member) => {
                // Find the current user's role by checking teamMembers array
                const currentUserRole = teamMembers.find(m => m.id === decodedToken?.id)?.role;
               // Debug log
                
                return (
                    <div key={member.id} className="grid grid-cols-4 gap-4 py-4 border-b">
                        <div>{member.email}</div>
                        <div>{member.role}</div>
                        <div>{member.status}</div>
                        <div>
                            {currentUserRole === 'admin' && (
                                <button 
                                    onClick={() => member.status === 'Pending' ? deleteInvite(member) : removeMember(member)}
                                    className="px-4 py-2 -ml-4 text-sm font-medium text-gray-600 hover:text-red-800 rounded-lg border hover:bg-red-50 transition-colors"
                                >
                                    Remove
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
          </div>

          {/* Billing Section */}
          <h2 className="text-xl font-semibold mb-6 mt-8">Billing</h2>
          <div className="bg-white border-gray-800 rounded-lg shadow-md p-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium mb-2">Subscription & Billing</h3>
                <p className="text-gray-600 text-sm">Manage your subscription and billing details</p>
              </div>
              <button 
                onClick={() => navigate('/pricing')}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Manage Billing
              </button>
            </div>
          </div>
        </div>
      )}

      </ContentBackground>
      </div>
    </div>
  );
};

export default Settings; 